<template>
  <div class="container">
    <canvas ref="canvas" class="particle-canvas"></canvas>
    <div class="progress-bar">
      <div class="progress-fill" :style="{ width: progress + '%' }"></div>
    </div>
    <button 
      ref="rewardButton"
      class="reward-button"
      @click="handleClick"
      :style="{ background: buttonBackground }"
    >
      {{ buttonText }}
      <div class="streak-counter">Streak: {{ streakCount }}</div>
    </button>
    <div v-if="showIllusion" class="optical-illusion"></div>
  </div>
</template>

<script>
export default {
  name: 'JoyPage',
  data() {
    return {
      progress: 0,
      anticipationFactor: 1,
      buttonBackground: 'linear-gradient(45deg, #ff6b6b, #ff8e8e)',
      buttonText: 'Generate Joy',
      streakCount: 0,
      lastClickTime: 0,
      showIllusion: false,
      canvas: null,
      ctx: null,
      particles: [],
      animationFrame: null,
      // Base reward definitions – each “particles” number is a base value
      rewards: [
        { color: '#ff6b6b', sound: 'bell', particles: 20, shape: 'circle', vibration: [50] },
        { color: '#4ecdc4', sound: 'chime', particles: 30, shape: 'star', vibration: [100, 50, 100] },
        { color: '#ffe66d', sound: 'ping', particles: 40, shape: 'heart', vibration: [150] },
        { color: '#6b6bff', sound: 'bell', particles: 25, shape: 'triangle', vibration: [200] }
      ],
      // Streak rewards – if you’re on a high streak, you get a premium reward!
      streakRewards: [
        { streak: 10, multiplier: 5, color: '#cc00ff', particles: 1000 },
        { streak: 5, multiplier: 3, color: '#ff6600' },
        { streak: 3, multiplier: 2, color: '#ffcc00' }
      ],
      // New queue system for successive clicks
      clickQueue: [],
      isProcessingClick: false
    };
  },
  mounted() {
    this.initCanvas();
    window.addEventListener('resize', this.handleResize);
    this.drawParticles();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    cancelAnimationFrame(this.animationFrame);
  },
  methods: {
    initCanvas() {
      this.canvas = this.$refs.canvas;
      this.ctx = this.canvas.getContext('2d');
      this.handleResize();
    },
    handleResize() {
      this.canvas.width = window.innerWidth;
      this.canvas.height = window.innerHeight;
    },
    // Create reward particles with randomized properties so that each particle differs
    createParticles(x, y, count, color, shape) {
      for (let i = 0; i < count; i++) {
        this.particles.push({
          x,
          y,
          color,
          shape,
          size: Math.random() * 5 + 3,           // Size between 3 and 8
          life: 1,
          fadeRate: Math.random() * 0.03 + 0.01,   // Fade speed between 0.01 and 0.04 per frame
          velocity: {
            x: (Math.random() - 0.5) * (Math.random() * 8 + 2),
            y: (Math.random() - 0.5) * (Math.random() * 8 + 2)
          },
          angle: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.2,
          gravity: Math.random() * 0.1           // Slight gravity effect
        });
      }
    },
    // Create celebration particles (stars across the screen) with dynamic behavior
    createCelebration(count) {
      for (let i = 0; i < count; i++) {
        const x = Math.random() * this.canvas.width;
        const y = Math.random() * this.canvas.height;
        const starColor = `hsl(${Math.floor(Math.random() * 360)}, 100%, 50%)`;
        this.particles.push({
          x,
          y,
          color: starColor,
          shape: 'star',
          size: Math.random() * 7 + 5,           // Size between 5 and 12
          life: 1,
          fadeRate: Math.random() * 0.02 + 0.01,
          velocity: {
            x: (Math.random() - 0.5) * (Math.random() * 6 + 2),
            y: (Math.random() - 0.5) * (Math.random() * 6 + 2)
          },
          angle: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.3,
          gravity: Math.random() * 0.1
        });
      }
    },
    // Main animation loop for particles – updating physics and drawing each frame
    drawParticles() {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      for (let i = this.particles.length - 1; i >= 0; i--) {
        const p = this.particles[i];
        p.x += p.velocity.x;
        p.y += p.velocity.y;
        p.velocity.y += p.gravity;
        p.angle += p.rotationSpeed;
        p.life -= p.fadeRate;
        
        this.ctx.save();
        this.ctx.translate(p.x, p.y);
        this.ctx.rotate(p.angle);
        this.ctx.globalAlpha = Math.max(p.life, 0);
        this.ctx.fillStyle = p.color;
        this.ctx.beginPath();
        if (p.shape === 'star') {
          const outerRadius = p.size;
          const innerRadius = p.size / 2;
          this.drawStar(0, 0, innerRadius, outerRadius, 5);
        } else if (p.shape === 'heart') {
          this.drawHeart(0, 0, p.size);
        } else if (p.shape === 'triangle') {
          this.drawTriangle(0, 0, p.size);
        } else {
          this.ctx.arc(0, 0, p.size, 0, Math.PI * 2);
        }
        this.ctx.fill();
        this.ctx.restore();
        
        if (p.life <= 0 || p.x < -50 || p.x > this.canvas.width + 50 || p.y < -50 || p.y > this.canvas.height + 50) {
          this.particles.splice(i, 1);
        }
      }
      this.animationFrame = requestAnimationFrame(this.drawParticles);
    },
    // Draw a star shape (centered at 0,0)
    drawStar(x, y, innerRadius, outerRadius, points) {
      const step = Math.PI / points;
      this.ctx.moveTo(0, -outerRadius);
      for (let i = 0; i < 2 * points; i++) {
        const r = (i % 2 === 0) ? outerRadius : innerRadius;
        const a = i * step - Math.PI / 2;
        this.ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      this.ctx.closePath();
    },
    // Draw a heart shape using Bézier curves
    drawHeart(x, y, size) {
      this.ctx.moveTo(0, -size / 2);
      this.ctx.bezierCurveTo(size, -size, size * 1.5, size / 3, 0, size);
      this.ctx.bezierCurveTo(-size * 1.5, size / 3, -size, -size, 0, -size / 2);
      this.ctx.closePath();
    },
    // Draw an equilateral triangle
    drawTriangle(x, y, size) {
      this.ctx.moveTo(0, -size);
      this.ctx.lineTo(size, size);
      this.ctx.lineTo(-size, size);
      this.ctx.closePath();
    },
    // Gamified “pop” effect on the reward button
    animateButton() {
      const button = this.$refs.rewardButton;
      button.classList.add('clicked');
      setTimeout(() => {
        button.classList.remove('clicked');
      }, 150);
    },
    // The click handler now pushes click details to a queue
    handleClick() {
      this.animateButton();
      // Enqueue the click event and process if not already busy
      this.clickQueue.push({ time: Date.now() });
      this.processClickQueue();
    },
    // Process the next click in the queue if none is currently executing
    processClickQueue() {
      if (this.isProcessingClick || this.clickQueue.length === 0) return;
      this.isProcessingClick = true;
      // Dequeue one click (its data can be extended as needed)
      this.clickQueue.shift();
      
      const currentTime = Date.now();
      const timeSinceLastClick = currentTime - this.lastClickTime;
      this.streakCount = timeSinceLastClick < 1000 ? this.streakCount + 1 : 1;
      this.lastClickTime = currentTime;
      if (navigator.vibrate) {
        navigator.vibrate(30);
      }
      
      // Determine reward based on streak and pick a random reward definition
      const streakReward = this.streakRewards.find(r => this.streakCount >= r.streak) || {};
      const reward = this.rewards[Math.floor(Math.random() * this.rewards.length)];
      
      // Increase progress factoring in any streak multiplier
      this.progress += (10 * this.anticipationFactor * (streakReward.multiplier || 1));
      
      const buttonRect = this.$refs.rewardButton.getBoundingClientRect();
      const buttonX = buttonRect.left + buttonRect.width / 2;
      const buttonY = buttonRect.top + buttonRect.height / 2;
      
      // If progress is full, trigger premium reward and celebration concurrently
      if (this.progress >= 100) {
        this.progress = 0;
        this.anticipationFactor += 0.2;
        const premiumParticles = streakReward.particles || 500;
        this.createParticles(window.innerWidth / 2, window.innerHeight / 2, premiumParticles, streakReward.color || '#ffe66d', 'star');
        const celebrationCount = Math.floor(Math.random() * 50) + 50;
        this.createCelebration(celebrationCount);
        if (navigator.vibrate) {
          navigator.vibrate([200, 100, 200]);
        }
        // Premium rewards use a longer delay before processing the next click
        setTimeout(() => {
          this.isProcessingClick = false;
          this.processClickQueue();
        }, 1200);
      } else {
        // Normal reward spawns particles at the button’s center
        const particleCount = reward.particles * (streakReward.multiplier || 1);
        this.createParticles(buttonX, buttonY, particleCount, streakReward.color || reward.color, reward.shape);
        if (navigator.vibrate) {
          navigator.vibrate(reward.vibration);
        }
        setTimeout(() => {
          this.isProcessingClick = false;
          this.processClickQueue();
        }, 600);
      }
      
      // Occasionally show an optical illusion overlay
      if (Math.random() < 0.02) {
        this.showIllusion = true;
        setTimeout(() => {
          this.showIllusion = false;
        }, 800);
      }
    }
  }
}
</script>

<style scoped>
.container {
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.particle-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.progress-bar {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 300px;
  height: 10px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  z-index: 3;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6b6b, #ff8e8e);
  border-radius: 5px;
  transition: width 0.3s ease;
}

.reward-button {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  padding: 20px 40px;
  font-size: 24px;
  border: none;
  border-radius: 50px;
  color: white;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 0 20px rgba(255, 107, 107, 0.4);
  z-index: 2;
  overflow: visible;
  user-select: none;
}

.reward-button:hover {
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow: 0 0 40px rgba(255, 107, 107, 0.6);
}

.reward-button:active {
  transform: translate(-50%, -50%) scale(0.95);
}

.reward-button.clicked {
  animation: buttonPop 0.15s ease-out;
}

@keyframes buttonPop {
  0% { transform: translate(-50%, -50%) scale(1); }
  50% { transform: translate(-50%, -50%) scale(0.9) rotate(3deg); }
  100% { transform: translate(-50%, -50%) scale(1); }
}

.streak-counter {
  position: absolute;
  bottom: -30px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 14px;
  white-space: nowrap;
}

.optical-illusion {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: repeating-radial-gradient(circle, black, black 10px, white 10px, white 20px);
  animation: illusion 1s infinite;
  z-index: 1;
  pointer-events: none;
  opacity: 0.7;
}

@keyframes illusion {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}
</style>

<style>
body {
  margin: 0;
  background: linear-gradient(45deg, #1a1a1a, #2a2a2a);
  overflow: hidden;
  font-family: 'Arial', sans-serif;
}

.neuron {
  position: absolute;
  width: 20px;
  height: 20px;
  pointer-events: none;
  transition: all 0.5s ease-out;
}

.neuron.star {
  clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
}

.neuron.heart {
  clip-path: polygon(50% 0%, 100% 30%, 100% 100%, 50% 70%, 0% 100%, 0% 30%);
}

.neuron.triangle {
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
}

.celebration {
  position: fixed;
  pointer-events: none;
  animation: spark 1s ease-out forwards;
}

@keyframes spark {
  0% { transform: scale(0); opacity: 1; }
  100% { transform: scale(2); opacity: 0; }
}

@keyframes float {
  0% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0); }
}
</style>
